import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Badge, Button, Dropdown } from 'antd';
import {
    BellOutlined,
    GiftOutlined,
    RedEnvelopeOutlined,
    TrophyOutlined,
    WalletOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './NotificationBell.css';
import { AppApc } from '../../api/appApc';
import { formatDateTime } from '../../i18n/format';
import { getIntlLocale } from '../../i18n/config';
import i18n from '../../i18n';

type NoticeIconType = 'redPacket' | 'topup' | 'giftCard' | 'cashback' | 'voucher' | 'default';

type NotificationItem = {
    id: string;
    type: string;
    title: string;
    body: string;
    refId: string | null;
    readAt: string | null;
    createdAt: string;
    seen: boolean;
};

const NOTICE_ICONS: Record<NoticeIconType, ReactNode> = {
    redPacket: <RedEnvelopeOutlined />,
    topup: <WalletOutlined />,
    giftCard: <GiftOutlined />,
    cashback: <TrophyOutlined />,
    voucher: <GiftOutlined />,
    default: <BellOutlined />,
};

function isUnread(item: NotificationItem) {
    return !item.seen && !item.readAt;
}

function getNoticeIconType(type: string): NoticeIconType {
    const value = type.toUpperCase();
    if (value.includes('CASHBACK')) return 'cashback';
    if (value.includes('RED_PACKET') || value.includes('REDPACKET')) return 'redPacket';
    if (value.includes('VOUCHER')) return 'voucher';
    if (value.includes('GIFT')) return 'giftCard';
    if (value.includes('TOPUP') || value.includes('DEPOSIT') || value.includes('WALLET')) {
        return 'topup';
    }
    return 'default';
}

function formatNoticeTime(value: string) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const diffMs = date.getTime() - Date.now();
    const absMs = Math.abs(diffMs);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (absMs < day) {
        const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language);
        const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
        if (absMs < minute) return rtf.format(Math.round(diffMs / 1000), 'second');
        if (absMs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
        return rtf.format(Math.round(diffMs / hour), 'hour');
    }

    return formatDateTime(date);
}

function mapNotification(raw: unknown): NotificationItem | null {
    if (!raw || typeof raw !== 'object') return null;
    const item = raw as Record<string, unknown>;
    if (typeof item.id !== 'string') return null;

    return {
        id: item.id,
        type: typeof item.type === 'string' ? item.type : '',
        title: typeof item.title === 'string' ? item.title : '',
        body: typeof item.body === 'string' ? item.body : '',
        refId: typeof item.refId === 'string' ? item.refId : null,
        readAt: typeof item.readAt === 'string' ? item.readAt : null,
        createdAt: typeof item.createdAt === 'string' ? item.createdAt : '',
        seen: Boolean(item.seen),
    };
}

function markItemRead(item: NotificationItem): NotificationItem {
    if (!isUnread(item)) return item;
    return {
        ...item,
        seen: true,
        readAt: item.readAt || new Date().toISOString(),
    };
}

function NotificationBell() {
    const { t } = useTranslation('common');
    const [items, setItems] = useState<NotificationItem[]>([]);
    const [pendingIds, setPendingIds] = useState<string[]>([]);
    const unread = useMemo(() => items.filter(isUnread).length, [items]);

    const getNotificationList = useCallback(async () => {
        try {
            const response = await AppApc.GetNotificationList();
            const payload = response?.data?.data;
            const list: unknown[] = Array.isArray(payload) ? payload : [];
            setItems(list.map(mapNotification).filter((item): item is NotificationItem => Boolean(item)));
        } catch (error) {
            console.error(error);
        }
    }, []);

    useEffect(() => {
        getNotificationList();
    }, [getNotificationList]);

    const markRead = async (id: string) => {
        const target = items.find((item) => item.id === id);
        if (!target || !isUnread(target) || pendingIds.includes(id)) return;

        setPendingIds((current) => [...current, id]);
        setItems((current) => current.map((item) => (item.id === id ? markItemRead(item) : item)));

        try {
            await AppApc.MarkNotificationAsRead({ id });
        } catch (error) {
            console.error(error);
            setItems((current) => current.map((item) => (item.id === id ? target : item)));
        } finally {
            setPendingIds((current) => current.filter((itemId) => itemId !== id));
        }
    };

    const markAllRead = async () => {
        const unreadItems = items.filter((item) => isUnread(item) && !pendingIds.includes(item.id));
        if (unreadItems.length === 0) return;

        const unreadIds = unreadItems.map((item) => item.id);
        setPendingIds((current) => [...current, ...unreadIds]);
        setItems((current) => current.map(markItemRead));

        const failedIds = new Set<string>();
        await Promise.all(
            unreadItems.map(async (item) => {
                try {
                    await AppApc.MarkNotificationAsRead({ id: item.id });
                } catch (error) {
                    console.error(error);
                    failedIds.add(item.id);
                }
            })
        );

        if (failedIds.size > 0) {
            const originalById = new Map(unreadItems.map((item) => [item.id, item]));
            setItems((current) =>
                current.map((item) => (failedIds.has(item.id) ? originalById.get(item.id) || item : item))
            );
        }

        setPendingIds((current) => current.filter((itemId) => !unreadIds.includes(itemId)));
    };

    return (
        <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlayClassName="notification-bell__dropdown"
            dropdownRender={() => (
                <div className="notification-bell__panel">
                    <div className="notification-bell__head">
                        <strong>{t('notifications.title')}</strong>
                        {unread > 0 ? (
                            <button type="button" onClick={markAllRead}>
                                {t('notifications.markAll')}
                            </button>
                        ) : null}
                    </div>
                    {items.length === 0 ? (
                        <p className="notification-bell__empty">{t('notifications.empty')}</p>
                    ) : (
                        <ul className="notification-bell__list">
                            {items.map((item) => {
                                const iconType = getNoticeIconType(item.type);
                                const unreadItem = isUnread(item);

                                return (
                                    <li key={item.id}>
                                        <button
                                            type="button"
                                            className={unreadItem ? 'is-unread' : ''}
                                            onClick={() => markRead(item.id)}
                                        >
                                            <span className={`notification-bell__icon notification-bell__icon--${iconType}`}>
                                                {NOTICE_ICONS[iconType]}
                                            </span>
                                            <span>
                                                <strong>{item.title}</strong>
                                                <em>{item.body}</em>
                                                <small>{formatNoticeTime(item.createdAt)}</small>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        >
            <Button
                className="notification-bell"
                aria-label={t('notifications.a11y', { count: unread })}
            >
                <Badge count={unread} size="small" overflowCount={9}>
                    <BellOutlined />
                </Badge>
            </Button>
        </Dropdown>
    );
}

export default NotificationBell;
